import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Section, StateView, useAuth, useConfirm, useToast } from "@ozpanel/ui";
import { Field, TextAreaField } from "./Form";

// MailboxIdentityPanel — one mailbox's identity (internal/modules/mail/mailbox_identity.go): the
// owner's name, title, phone, plain-text signature and photo. ONE component for the three places it is
// edited — the mailbox app (the person themselves), the account console and the admin console (on the
// mailbox's behalf) — so a field cannot exist in one and not the others. The server decides who may
// touch which mailbox; this only chooses the address:
//
//   no domain/localpart  → /mail/me/identity (the signed-in mailbox, resolved from the session)
//   domain + localpart   → /mail/domains/{domain}/mailboxes/{localpart}/identity
//
// Where it is read: Roundcube sets the identity's name and signature at each sign-in (the panel's
// value wins when it has one; an empty field here leaves the client's own), SOGo shows the name.

export interface MailboxIdentity {
  address: string;
  full_name: string;
  title: string;
  phone: string;
  signature: string;
  /** null when the mailbox has no photo. */
  avatar: { type: string; sha256: string; bytes: number } | null;
  /** false: no identity has ever been stored for this mailbox. */
  stored: boolean;
}

type Draft = Pick<MailboxIdentity, "full_name" | "title" | "phone" | "signature">;
const FIELDS: (keyof Draft)[] = ["full_name", "title", "phone", "signature"];

// The limits the server enforces, checked here first only so the message comes before the upload.
const AVATAR_MAX = 256 * 1024;
const AVATAR_TYPES = ["image/png", "image/jpeg", "image/webp"];

type Client = ReturnType<typeof useAuth>["client"];

/** The identity routes of one mailbox — every call written out, so each is visible where it is made. */
interface IdentityApi {
  key: string;
  get(): Promise<MailboxIdentity>;
  put(body: Partial<Draft>): Promise<MailboxIdentity>;
  upload(f: File): Promise<MailboxIdentity>;
  removePhoto(): Promise<MailboxIdentity>;
  photoURL(): string;
}

function identityApi(client: Client, domain?: string, localpart?: string): IdentityApi {
  // The body of an upload IS the image; the server decides its type from the bytes.
  const image = (f: File) => ({ body: f, headers: { "Content-Type": f.type } });
  if (domain && localpart) {
    const d = encodeURIComponent(domain);
    const l = encodeURIComponent(localpart);
    return {
      key: `${domain}/${localpart}`,
      get: () => client.apiFetch<MailboxIdentity>(`/mail/domains/${d}/mailboxes/${l}/identity`),
      put: (body) => client.apiFetch<MailboxIdentity>(`/mail/domains/${d}/mailboxes/${l}/identity`, { method: "PUT", body: JSON.stringify(body) }),
      upload: (f) => client.apiFetch<MailboxIdentity>(`/mail/domains/${d}/mailboxes/${l}/identity/avatar`, { ...image(f), method: "POST" }),
      removePhoto: () => client.apiFetch<MailboxIdentity>(`/mail/domains/${d}/mailboxes/${l}/identity/avatar`, { method: "DELETE" }),
      photoURL: () => client.apiURL(`/mail/domains/${d}/mailboxes/${l}/identity/avatar`),
    };
  }
  return {
    key: "me",
    get: () => client.apiFetch<MailboxIdentity>("/mail/me/identity"),
    put: (body) => client.apiFetch<MailboxIdentity>("/mail/me/identity", { method: "PUT", body: JSON.stringify(body) }),
    upload: (f) => client.apiFetch<MailboxIdentity>("/mail/me/identity/avatar", { ...image(f), method: "POST" }),
    removePhoto: () => client.apiFetch<MailboxIdentity>("/mail/me/identity/avatar", { method: "DELETE" }),
    photoURL: () => client.apiURL("/mail/me/identity/avatar"),
  };
}

function initials(name: string, address: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0] ?? "";
  const last = words[words.length - 1] ?? "";
  if (words.length >= 2) return (first.slice(0, 1) + last.slice(0, 1)).toUpperCase();
  if (words.length === 1) return first.slice(0, 2).toUpperCase();
  return address.slice(0, 1).toUpperCase();
}

export function MailboxIdentityPanel({
  domain,
  localpart,
  title = "Identity",
  self = false,
}: {
  domain?: string;
  localpart?: string;
  title?: string;
  /** The mailbox owner editing themselves: the copy says "you". */
  self?: boolean;
}) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const api = identityApi(client, domain, localpart);
  const KEY = ["mailbox-identity", api.key];
  const ident = useQuery({ queryKey: KEY, queryFn: api.get, retry: false });
  const [draft, setDraft] = React.useState<Draft | null>(null);
  const [busy, setBusy] = React.useState(false);
  const fileRef = React.useRef<HTMLInputElement>(null);
  const confirm = useConfirm();

  const cur = ident.data;
  const value: Draft = draft ?? {
    full_name: cur?.full_name ?? "",
    title: cur?.title ?? "",
    phone: cur?.phone ?? "",
    signature: cur?.signature ?? "",
  };
  const changed = cur ? FIELDS.filter((k) => value[k] !== cur[k]) : [];
  const set = (k: keyof Draft) => (v: string) => setDraft({ ...value, [k]: v });

  const done = (data: MailboxIdentity, msg: string) => {
    qc.setQueryData(KEY, data);
    setDraft(null);
    notify({ kind: "success", title: msg });
  };
  const fail = (what: string, e: unknown) =>
    notify({ kind: "error", title: what, body: e instanceof Error ? e.message : String(e) });

  const save = async () => {
    const body: Partial<Draft> = {};
    for (const k of changed) body[k] = value[k];
    setBusy(true);
    try {
      done(await api.put(body), "Identity saved");
    } catch (e) {
      fail("Could not save the identity", e);
    } finally {
      setBusy(false);
    }
  };

  const upload = async (f: File) => {
    if (!AVATAR_TYPES.includes(f.type)) return fail("That photo cannot be used", new Error("Use a PNG, JPEG or WebP image."));
    if (f.size > AVATAR_MAX) return fail("That photo is too large", new Error("The photo must be 256 KB or smaller."));
    setBusy(true);
    try {
      done(await api.upload(f), "Photo saved");
    } catch (e) {
      fail("Could not save the photo", e);
    } finally {
      setBusy(false);
    }
  };

  const removePhoto = async () => {
    setBusy(true);
    try {
      done(await api.removePhoto(), "Photo removed");
    } catch (e) {
      fail("Could not remove the photo", e);
    } finally {
      setBusy(false);
    }
  };

  const who = self ? "you" : "this mailbox's owner";
  return (
    <Section
      title={title}
      actions={
        <Button size="sm" variant="accent" disabled={busy || changed.length === 0} onClick={() => void save()}>
          Save
        </Button>
      }
    >
      <StateView loading={ident.isLoading} error={ident.error} errorContext="the identity" onRetry={() => void ident.refetch()}>
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex shrink-0 flex-row items-center gap-3 sm:w-36 sm:flex-col sm:items-start">
            {cur?.avatar ? (
              <img
                src={api.photoURL() + "?v=" + cur.avatar.sha256.slice(0, 16)}
                alt={`Photo of ${cur.full_name || cur.address}`}
                className="size-20 rounded-full border border-line object-cover"
              />
            ) : (
              <div
                aria-hidden
                className="grid size-20 place-items-center rounded-full bg-accentsoft text-[22px] font-semibold text-accent"
              >
                {initials(value.full_name, cur?.address ?? "?")}
              </div>
            )}
            <div className="flex flex-wrap gap-1.5">
              <input
                ref={fileRef}
                type="file"
                accept={AVATAR_TYPES.join(",")}
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) void upload(f);
                }}
              />
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => fileRef.current?.click()}>
                {cur?.avatar ? "Change photo" : "Add photo"}
              </Button>
              {cur?.avatar && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() =>
                    confirm.ask(
                      { title: "Remove the photo?", body: "Mail apps show initials instead until a new one is added.", confirmLabel: "Remove photo" },
                      removePhoto,
                    )
                  }
                >
                  Remove
                </Button>
              )}
            </div>
            <div className="hidden text-[11.5px] leading-relaxed text-muted sm:block">PNG, JPEG or WebP, up to 256 KB.</div>
          </div>
          <div className="grid min-w-0 flex-1 gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Field
                label="Full name"
                value={value.full_name}
                onChange={set("full_name")}
                placeholder={cur?.address}
                autoComplete="name"
                help={`The name mail apps show on what ${who} send${self ? "" : "s"}. Empty: the address alone.`}
              />
            </div>
            <Field label="Title" value={value.title} onChange={set("title")} autoComplete="organization-title" />
            <Field label="Phone" value={value.phone} onChange={set("phone")} autoComplete="tel" inputMode="tel" />
            <div className="sm:col-span-2">
              <TextAreaField
                label="Signature"
                value={value.signature}
                onChange={set("signature")}
                rows={4}
                help="Plain text, added under each message by the webmail apps."
              />
            </div>
          </div>
        </div>
        <p className="mt-3 text-[12px] leading-relaxed text-muted">
          {cur && !cur.stored && !draft ? "Nothing has been set yet. " : ""}
          Roundcube takes the name and signature at its next sign-in; a field left empty here leaves what was set
          in Roundcube itself. SOGo shows the name.
        </p>
      </StateView>
      {confirm.dialog}
    </Section>
  );
}
